import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Check } from 'lucide-react';
import { toast } from 'sonner';
import { PayPalWrapper as PayPalCheckout } from '../components/PayPalWrapper';
import SEOHead from '../components/SEOHead';
import { trackAdsConversion, trackQualifyLead } from '../utils/analytics';

const API_URL = "";

// Default tiers for initial render
const defaultMembershipTiers = [
  {
    id: 1,
    name: "Annual Membership",
    price: 60,
    period: "per year",
    benefits: [
      "Full privilege membership with voting and office holding rights",
      "Access to exhibit in the CAFA gallery and exhibitions",
      "Discounted rates on classes and workshops"
    ],
    featured: true
  },
  {
    id: 2,
    name: "Lifetime Membership",
    price: 500,
    period: "one-time payment",
    benefits: [
      "All Annual Membership benefits for life",
      "Full privilege membership with voting and office holding rights",
      "Priority registration for workshops"
    ],
    featured: false
  }
];

export const Membership = () => {
  const [membershipTiers, setMembershipTiers] = useState(defaultMembershipTiers);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    membershipType: ''
  });
  const [showPayPal, setShowPayPal] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);

  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const res = await fetch(`${API_URL}/api/content/membership-tiers`);
        if (res.ok) {
          setMembershipTiers(await res.json());
        }
      } catch (err) {
        console.error('Error fetching membership tiers:', err);
      }
    };
    fetchTiers();
  }, []);

  const handleSelectPlan = (tier) => {
    setFormData({ ...formData, membershipType: tier.name });
    setSelectedTier(tier);
    setShowPayPal(false);
  };

  const handleProceedToPayment = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.membershipType) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    const tier = membershipTiers.find(t => t.name === formData.membershipType);
    if (!tier) {
      toast.error('Please select a membership type');
      return;
    }
    
    setSelectedTier(tier);
    setShowPayPal(true);
    trackAdsConversion();
    trackQualifyLead({ membership_type: tier.name, value: tier.price, currency: 'USD' });
  };

  const getMembershipItems = () => {
    if (!selectedTier) return [];
    
    return [{
      name: `${selectedTier.name} Membership`,
      description: `Coppini Academy of Fine Art - ${selectedTier.period}`,
      quantity: 1,
      price: selectedTier.price,
      category: 'membership'
    }];
  };

  const handlePaymentSuccess = (result) => {
    // Store membership application
    const applicationData = {
      ...formData,
      timestamp: new Date().toISOString(),
      membershipPrice: selectedTier?.price,
      membershipPeriod: selectedTier?.period,
      paymentId: result.payment_id,
      status: 'paid'
    };
    
    localStorage.setItem('membership_application', JSON.stringify(applicationData));
    
    toast.success('Welcome to the Coppini Academy!', {
      description: 'Your membership is now active. Check your email for confirmation.'
    });
    
    setFormData({ name: '', email: '', phone: '', membershipType: '' });
    setShowPayPal(false);
    setSelectedTier(null);
  };

  const handlePaymentError = (error) => {
    toast.error('Payment failed. Please try again.');
    console.error('Payment error:', error);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setShowPayPal(false);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="membership-page">
      <SEOHead
        title="Membership — Join the Academy"
        description="Become a member of The Coppini Academy of Fine Art. Artist, patron, and student memberships with gallery exhibition opportunities, class discounts, and more."
        canonicalPath="/membership"
      />
      {/* Hero - Dark bar with reversed type */}
      <section className="bg-stone-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-serif text-white mb-6">Membership</h1>
          <p className="text-xl text-stone-300">
            Join our community and enjoy exclusive benefits
          </p>
        </div>
      </section>

      {/* Why Become a Member - Reduced top padding */}
      <section className="pt-8 pb-12 bg-stone-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-8 text-center">Why Become a Member?</h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 items-center">
            {/* Image on left - smaller */}
            <div className="order-2 md:order-1 md:col-span-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_artsite-debug/artifacts/bt7s384y_472234898_10163129347030229_3744966959464305780_n.jpg"
                alt="Members gathering at The Coppini Academy"
                className="w-full rounded-lg shadow-lg"
              />
            </div>
            {/* Copy on right */}
            <div className="order-1 md:order-2 md:col-span-3 space-y-6">
              <p className="text-lg text-stone-700 leading-relaxed">
                Membership at The Coppini Academy provides you with access to a vibrant community of artists,
                world-class instruction, and opportunities to showcase your work.
              </p>
              <p className="text-lg text-stone-700 leading-relaxed">
                Whether you're a beginner looking to learn classical techniques or an experienced artist
                seeking to refine your skills, our membership options are designed to support your artistic journey.
              </p>
              <p className="text-lg text-stone-700 leading-relaxed">
                Questions? Email us at{' '}
                <a href="mailto:membership@thecoppini.org" className="text-cafa-red hover:underline font-medium">
                  membership@thecoppini.org
                </a>
                {' '}for more details.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Tiers */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {membershipTiers.map((tier) => (
              <Card 
                key={tier.id} 
                className={`border-2 transition-all hover:shadow-xl relative ${
                  tier.featured 
                    ? 'border-cafa-red shadow-lg scale-105' 
                    : 'border-stone-200'
                } ${selectedTier?.id === tier.id ? 'ring-2 ring-cafa-red' : ''}`}
              >
                <CardHeader className="text-center pb-8">
                  {tier.featured && (
                    <div className="absolute top-0 right-0 bg-cafa-red text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                      POPULAR
                    </div>
                  )}
                  <CardTitle className="text-2xl font-serif text-stone-900 mb-4">
                    {tier.name}
                  </CardTitle>
                  <div className="text-4xl font-bold text-cafa-red mb-2">
                    ${tier.price}
                  </div>
                  <p className="text-stone-500">{tier.period}</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4 mb-8">
                    {tier.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check size={20} className="text-cafa-red flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-stone-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    onClick={() => handleSelectPlan(tier)}
                    className={`w-full ${
                      selectedTier?.id === tier.id
                        ? 'bg-cafa-red-900 text-white'
                        : tier.featured
                        ? 'bg-cafa-red hover:bg-cafa-red-900 text-white'
                        : 'bg-stone-100 hover:bg-stone-200 text-stone-900'
                    }`}
                    data-testid={`select-plan-${tier.id}`}
                  >
                    {selectedTier?.id === tier.id ? 'Selected' : 'Select Plan'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-serif text-stone-900 mb-4">Join Now</h2>
            <p className="text-stone-600">Complete your membership registration</p>
          </div>

          <Card className="border-stone-200">
            <CardContent className="p-8">
              <form onSubmit={handleProceedToPayment} className="space-y-6">
                <div>
                  <Label htmlFor="name" className="text-stone-700">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-2"
                    placeholder="John Doe"
                    data-testid="membership-name"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-stone-700">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-2"
                    placeholder="john@example.com"
                    data-testid="membership-email"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-stone-700">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-2"
                    placeholder="(210) 555-0123"
                  />
                </div>

                <div>
                  <Label htmlFor="membershipType" className="text-stone-700">Membership Type *</Label>
                  <select
                    id="membershipType"
                    name="membershipType"
                    value={formData.membershipType}
                    onChange={handleChange}
                    required
                    className="mt-2 w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cafa-red"
                    data-testid="membership-type-select"
                  >
                    <option value="">Select a membership type</option>
                    {membershipTiers.map((tier) => (
                      <option key={tier.id} value={tier.name}>
                        {tier.name} - ${tier.price}/{tier.period}
                      </option>
                    ))}
                  </select>
                </div>

                {showPayPal && selectedTier ? (
                  <div className="space-y-4 pt-4">
                    <PayPalCheckout
                      items={getMembershipItems()}
                      payerEmail={formData.email}
                      payerName={formData.name}
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                      onCancel={() => setShowPayPal(false)}
                      registrationData={{
                        type: 'membership',
                        member_info: formData,
                        membership_tier: selectedTier?.name,
                        membership_price: selectedTier?.price
                      }}
                    />
                    <Button 
                      type="button"
                      variant="outline"
                      onClick={() => setShowPayPal(false)}
                      className="w-full"
                    >
                      Back to Edit
                    </Button>
                  </div>
                ) : (
                  <Button 
                    type="submit" 
                    className="w-full bg-cafa-red hover:bg-cafa-red-900 text-white"
                    data-testid="membership-submit"
                  >
                    Proceed to Payment
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};
